import React, { createContext, useContext, useEffect, useState } from 'react';
import { db } from '../firebase';
import {
    collection,
    addDoc,
    onSnapshot,
    query,
    where,
    orderBy,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export function AppProvider({ children }) {
    const { user } = useAuth();

    const [products, setProducts] = useState([]);
    const [sales, setSales] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [clients, setClients] = useState([]);
    const [orders, setOrders] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    // Carregar dados do Firestore em tempo real
    useEffect(() => {
        if (!user) {
            setProducts([]);
            setSales([]);
            setExpenses([]);
            setClients([]);
            setOrders([]);
            setIngredients([]);
            return;
        }

        setLoadingData(true);

        const productsRef = collection(db, 'products');
        const salesRef = collection(db, 'sales');
        const expensesRef = collection(db, 'expenses');
        const clientsRef = collection(db, 'clients');
        const ordersRef = collection(db, 'orders');
        const ingredientsRef = collection(db, 'ingredients');

        const qProducts = query(productsRef, where('uid', '==', user.uid), orderBy('name'));
        const qSales = query(salesRef, where('uid', '==', user.uid), orderBy('date', 'desc'));
        const qExpenses = query(expensesRef, where('uid', '==', user.uid), orderBy('date', 'desc'));
        const qClients = query(clientsRef, where('uid', '==', user.uid), orderBy('name'));
        const qOrders = query(ordersRef, where('uid', '==', user.uid), orderBy('createdAt', 'desc'));
        const qIngredients = query(ingredientsRef, where('uid', '==', user.uid), orderBy('name'));

        const unsubProducts = onSnapshot(qProducts, (snap) => {
            setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const unsubSales = onSnapshot(qSales, (snap) => {
            setSales(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const unsubExpenses = onSnapshot(qExpenses, (snap) => {
            setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const unsubClients = onSnapshot(qClients, (snap) => {
            setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const unsubOrders = onSnapshot(qOrders, (snap) => {
            setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const unsubIngredients = onSnapshot(qIngredients, (snap) => {
            setIngredients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        const timeout = setTimeout(() => setLoadingData(false), 800);

        return () => {
            unsubProducts();
            unsubSales();
            unsubExpenses();
            unsubClients();
            unsubOrders();
            unsubIngredients();
            clearTimeout(timeout);
        };
    }, [user]);

    // ============ PRODUTOS ============
    const addProduct = async (product) => {
        if (!user) return;
        const ref = collection(db, 'products');
        await addDoc(ref, { ...product, uid: user.uid, createdAt: new Date().toISOString() });
    };

    const updateProduct = async (id, product) => {
        if (!user) return;
        const ref = doc(db, 'products', id);
        await updateDoc(ref, { ...product, updatedAt: new Date().toISOString() });
    };

    const deleteProduct = async (id) => {
        if (!user) return;
        const ref = doc(db, 'products', id);
        await deleteDoc(ref);
    };

    // ============ VENDAS ============
    const addSale = async (sale) => {
        if (!user) return;
        const ref = collection(db, 'sales');
        await addDoc(ref, { ...sale, uid: user.uid, createdAt: new Date().toISOString() });
    };

    // ============ DESPESAS ============
    const addExpense = async (expense) => {
        if (!user) return;
        const ref = collection(db, 'expenses');
        await addDoc(ref, { ...expense, uid: user.uid, createdAt: new Date().toISOString() });
    };

    const updateExpense = async (id, expense) => {
        if (!user) return;
        const ref = doc(db, 'expenses', id);
        await updateDoc(ref, { ...expense, updatedAt: new Date().toISOString() });
    };

    const deleteExpense = async (id) => {
        if (!user) return;
        const ref = doc(db, 'expenses', id);
        await deleteDoc(ref);
    };

    // ============ INGREDIENTES ============
    const addIngredient = async (ingredient) => {
        if (!user) return;
        const ref = collection(db, 'ingredients');
        await addDoc(ref, { ...ingredient, uid: user.uid, createdAt: new Date().toISOString() });
    };

    const updateIngredient = async (id, ingredient) => {
        if (!user) return;
        const ref = doc(db, 'ingredients', id);
        await updateDoc(ref, { ...ingredient, updatedAt: new Date().toISOString() });
    };

    const deleteIngredient = async (id) => {
        if (!user) return;
        const ref = doc(db, 'ingredients', id);
        await deleteDoc(ref);
    };

    // ============ CLIENTES ============
    const addClient = async (client) => {
        if (!user) return;
        const ref = collection(db, 'clients');
        await addDoc(ref, { ...client, uid: user.uid, createdAt: new Date().toISOString() });
    };

    const updateClient = async (id, client) => {
        if (!user) return;
        const ref = doc(db, 'clients', id);
        await updateDoc(ref, { ...client, updatedAt: new Date().toISOString() });
    };

    const deleteClient = async (id) => {
        if (!user) return;
        const ref = doc(db, 'clients', id);
        await deleteDoc(ref);
    };

    // ============ PEDIDOS ============
    const addOrder = async (order) => {
        if (!user) return;

        // Gerar número do pedido
        const orderNumber = `PED-${Date.now().toString().slice(-6)}`;

        const ref = collection(db, 'orders');
        const docRef = await addDoc(ref, {
            ...order,
            orderNumber,
            uid: user.uid,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });

        return { id: docRef.id, orderNumber };
    };

    const updateOrder = async (id, order) => {
        if (!user) return;
        const ref = doc(db, 'orders', id);
        await updateDoc(ref, { ...order, updatedAt: new Date().toISOString() });
    };

    const updateOrderStatus = async (id, status) => {
        if (!user) return;
        const ref = doc(db, 'orders', id);
        await updateDoc(ref, {
            status,
            updatedAt: new Date().toISOString(),
        });
    };

    const deleteOrder = async (id) => {
        if (!user) return;
        const ref = doc(db, 'orders', id);
        await deleteDoc(ref);
    };

    // Buscar pedido individual (para página pública)
    const getOrderById = async (orderId) => {
        const ref = doc(db, 'orders', orderId);
        const snapshot = await getDoc(ref);
        if (snapshot.exists()) {
            return { id: snapshot.id, ...snapshot.data() };
        }
        return null;
    };

    return (
        <AppContext.Provider
            value={{
                // Estados
                products,
                sales,
                expenses,
                clients,
                orders,
                ingredients,
                loadingData,

                // Produtos
                addProduct,
                updateProduct,
                deleteProduct,

                // Vendas
                addSale,

                // Despesas
                addExpense,
                updateExpense,
                deleteExpense,

                // Ingredientes
                ingredients,
                addIngredient,
                updateIngredient,
                deleteIngredient,

                // Clientes
                addClient,
                updateClient,
                deleteClient,

                // Pedidos
                addOrder,
                updateOrder,
                updateOrderStatus,
                deleteOrder,
                getOrderById,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useAppContext() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error('useAppContext must be used within AppProvider');
    return ctx;
}
